import { useCallback } from "react";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  isDevNavigableCycleId,
  useCycles,
} from "@src/packages/cycles/hooks/useCycles";
import type { CareStackParamList } from "../types";

/**
 * Resolve an explicit specimen cycle relationship against the patient's
 * cycle collection. The specimen adapter remains compatibility-backed until
 * the canonical clinical-link read layer is available to the patient app;
 * this hook deliberately never invents a target for an unresolved ID.
 */
export function useCycleNavigation(): (
  cycleId: string,
) => (() => void) | undefined {
  const navigation =
    useNavigation<NativeStackNavigationProp<CareStackParamList>>();
  const { data: cycles } = useCycles();

  return useCallback(
    (cycleId: string) => {
      const cycle = cycles?.find((candidate) => candidate.id === cycleId);
      const isDevFixture = isDevNavigableCycleId(cycleId);
      if (!cycle && !isDevFixture) return undefined;

      return () => {
        if (isDevFixture || cycle?.status === "open") {
          navigation.navigate("CycleDetail", { cycleId });
        } else {
          navigation.navigate("PastCycleDetail", { cycleId });
        }
      };
    },
    [cycles, navigation],
  );
}
